/**
 * Feature 4: User Session Analytics
 * Compares session metrics between purchasers and non-purchasers
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { Card, CardContent, Typography, CircularProgress, Alert, Tabs, Tab, Box } from '@mui/material';
import apiService from '../services/apiService';

const SessionAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getSessionAnalytics();
      setData(response.segments);
    } catch (err) {
      setError('Failed to load session analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent style={{ textAlign: 'center', padding: '40px' }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: '16px' }}>
            Loading session analytics...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">No data available</Alert>
        </CardContent>
      </Card>
    );
  }

  // Prepare comparison data
  const purchasers = data.find(s => s.user_segment === 'purchasers') || {};
  const nonPurchasers = data.find(s => s.user_segment === 'non_purchasers') || {};

  const comparisonData = [
    {
      metric: 'Avg Duration (min)',
      Purchasers: (purchasers.avg_session_duration_seconds / 60).toFixed(0),
      'Non-Purchasers': (nonPurchasers.avg_session_duration_seconds / 60).toFixed(0),
    },
    {
      metric: 'Avg Events',
      Purchasers: purchasers.avg_events_per_session?.toFixed(1),
      'Non-Purchasers': nonPurchasers.avg_events_per_session?.toFixed(1),
    },
  ];

  // Radar chart data
  const radarData = [
    {
      metric: 'Duration',
      Purchasers: (purchasers.avg_session_duration_seconds / 60 / 100).toFixed(1),
      'Non-Purchasers': (nonPurchasers.avg_session_duration_seconds / 60 / 100).toFixed(1),
    },
    {
      metric: 'Events',
      Purchasers: purchasers.avg_events_per_session?.toFixed(1),
      'Non-Purchasers': nonPurchasers.avg_events_per_session?.toFixed(1),
    },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          User Session Analytics
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Comparison of session behavior between purchasers and non-purchasers
        </Typography>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {/* Purchasers Card */}
          <div style={{ padding: '16px', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '2px solid #4CAF50' }}>
            <Typography variant="h6" style={{ color: '#2e7d32' }}>
              Purchasers
            </Typography>
            <div style={{ marginTop: '12px' }}>
              <Typography variant="body2" color="textSecondary">
                Total Users
              </Typography>
              <Typography variant="h5">
                {formatNumber(purchasers.total_users)}
              </Typography>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2" color="textSecondary">
                Total Sessions
              </Typography>
              <Typography variant="h6">
                {formatNumber(purchasers.total_sessions)}
              </Typography>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2" color="textSecondary">
                Avg Duration
              </Typography>
              <Typography variant="h6">
                {formatDuration(purchasers.avg_session_duration_seconds)}
              </Typography>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2" color="textSecondary">
                Avg Events/Session
              </Typography>
              <Typography variant="h6">
                {purchasers.avg_events_per_session?.toFixed(2)}
              </Typography>
            </div>
          </div>

          {/* Non-Purchasers Card */}
          <div style={{ padding: '16px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '2px solid #FF9800' }}>
            <Typography variant="h6" style={{ color: '#e65100' }}>
              Non-Purchasers
            </Typography>
            <div style={{ marginTop: '12px' }}>
              <Typography variant="body2" color="textSecondary">
                Total Users
              </Typography>
              <Typography variant="h5">
                {formatNumber(nonPurchasers.total_users)}
              </Typography>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2" color="textSecondary">
                Total Sessions
              </Typography>
              <Typography variant="h6">
                {formatNumber(nonPurchasers.total_sessions)}
              </Typography>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2" color="textSecondary">
                Avg Duration
              </Typography>
              <Typography variant="h6">
                {formatDuration(nonPurchasers.avg_session_duration_seconds)}
              </Typography>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="body2" color="textSecondary">
                Avg Events/Session
              </Typography>
              <Typography variant="h6">
                {nonPurchasers.avg_events_per_session?.toFixed(2)}
              </Typography>
            </div>
          </div>
        </div>

        {/* Tabs for different visualizations */}
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} style={{ marginBottom: '16px' }}>
          <Tab label="Bar Comparison" />
          <Tab label="Radar Chart" />
        </Tabs>

        {/* Bar Chart */}
        {activeTab === 0 && (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={comparisonData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="metric" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Purchasers" fill="#4CAF50" radius={[8, 8, 0, 0]} />
              <Bar dataKey="Non-Purchasers" fill="#FF9800" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}

        {/* Radar Chart */}
        {activeTab === 1 && (
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metric" />
              <PolarRadiusAxis />
              <Radar name="Purchasers" dataKey="Purchasers" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.6} />
              <Radar name="Non-Purchasers" dataKey="Non-Purchasers" stroke="#FF9800" fill="#FF9800" fillOpacity={0.6} />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        )}

        {/* Key Insights */}
        <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <Typography variant="subtitle2" gutterBottom>
            🔍 Key Insights
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>
              <Typography variant="body2">
                Purchasers spend{' '}
                <strong>
                  {((purchasers.avg_session_duration_seconds / nonPurchasers.avg_session_duration_seconds - 1) * 100).toFixed(0)}%
                </strong>{' '}
                more time per session
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Purchasers interact with{' '}
                <strong>
                  {((purchasers.avg_events_per_session / nonPurchasers.avg_events_per_session - 1) * 100).toFixed(0)}%
                </strong>{' '}
                more events per session
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Only{' '}
                <strong>
                  {((purchasers.total_users / (purchasers.total_users + nonPurchasers.total_users)) * 100).toFixed(1)}%
                </strong>{' '}
                of users make a purchase
              </Typography>
            </li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default SessionAnalytics;
